'use client';

import { useState } from 'react';
import type { Message, VideoMediaData } from '@repo/api';
import { MESSAGE_TYPES } from '@repo/utils';
import { IconCross, IconPlay } from '@repo/svgs';
import { useAnalytics } from '@/hooks/useAnalytics';

interface MessageVideoCardProps {
  message: Message;
}

const ORIENTATION_ASPECT_RATIO: Record<string, number> = {
  landscape: 16 / 9,
  portrait: 9 / 16,
  square: 1,
};

function getAspectRatio(data: VideoMediaData): number {
  if (data.orientation && ORIENTATION_ASPECT_RATIO[data.orientation]) {
    return ORIENTATION_ASPECT_RATIO[data.orientation]!;
  }
  if (data.aspectRatio && data.aspectRatio > 0) return data.aspectRatio;
  return ORIENTATION_ASPECT_RATIO.landscape!;
}

export function MessageVideoCard({ message }: MessageVideoCardProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const analytics = useAnalytics();

  const isMe = message.senderType === MESSAGE_TYPES.USER;

  // contentData is a mixed array — find the video item
  const mediaArray = message.contentData as (VideoMediaData | any)[] | undefined;
  const data = mediaArray?.find((d): d is VideoMediaData => d?.type === 'video');

  if (!data) return null;

  const aspectRatio = getAspectRatio(data);
  // thumbnail is an object (not array)
  const thumbnailUrl = data.thumbnail?.url;
  const videoUrl = data.url;

  const maxWidth = aspectRatio >= 1 ? 280 : 160;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (videoUrl) {
            void analytics.track('chat_video', {});
            setIsFullscreen(true);
          }
        }}
        className={`relative rounded-2xl overflow-hidden flex-shrink-0 ${isMe ? 'self-end ml-auto' : 'self-start ml-12'}`}
        style={{ width: maxWidth, aspectRatio }}
      >
        {thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbnailUrl}
            alt="Video thumbnail"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-neutral-800" />
        )}

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-3 bg-primary/90 rounded-full flex items-center justify-center">
            <IconPlay className="w-4 h-4 icon-primary" />
          </div>
        </div>
      </button>

      {/* Fullscreen video viewer */}
      {isFullscreen && videoUrl ? (
        <div
          className="fixed inset-0 z-60 bg-black flex flex-col"
          onClick={() => setIsFullscreen(false)}
        >
          {/* Close */}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setIsFullscreen(false);
            }}
            className="absolute top-[calc(env(safe-area-inset-top)+1.5rem)] right-4 lg:right-12 w-10 h-10 rounded-full z-30 flex items-center justify-center transition-colors pointer-events-auto"
            aria-label="Close"
          >
            <IconCross width={24} height={24} style={{ color: 'var(--icon-color-inverted)' }} />
          </button>

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center lg:p-20 p-6 pt-18 lg:pt-20 min-h-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative w-full h-full max-w-full max-h-full">
              <video
                src={videoUrl}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
